import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { message } from 'antd';
import { API } from 'api';
import { getPureParams } from '../utils/helpers';

const initialState = {};

export const tripSliceV2 = createSlice({
	name: 'tripStateV2',
	initialState,
	reducers: {},
});

const regx = /true/;

/**
 * Coerce one { lat, lng, time } point from `trip_map_path` into finite numbers.
 * Returns null for anything unusable so a single bad fix cannot break the polyline.
 * (0, 0) is a known GPS device-fault sentinel, not a real position, so it is rejected.
 */
function normalizeMapPathPoint(point) {
	const lat = Number(point?.lat);
	const lng = Number(point?.lng);

	if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
	if (lat === 0 && lng === 0) return null;
	if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

	return { lat, lng, time: point?.time || null };
}

// Helper function to get filtered data
function getFilterdAllData(rest) {
	return {
		is_valid: 'All',
		...getPureParams(rest),
	};
}

/**
 * Build the History `filter_by` exactly as the History search posts it.
 * Shared by getTripListActionV2 (search) and exportTripListV2 (export).
 * `convertFiltersToV2` is passed in (dynamic-imported by callers) to keep this pure.
 */
export const buildTripFilterByV2History = (
	{
		search_type,
		limit,
		offset,
		currentPage,
		sort_column,
		sort_direction,
		is_valid = 'All',
		...rest
	},
	convertFiltersToV2
) => {
	let filters = {};
	if (is_valid === 'All') {
		filters = getFilterdAllData(rest);
	} else if (is_valid === undefined || regx.test(is_valid)) {
		filters = { is_valid: true, ...getPureParams(rest) };
	} else if (regx.test(is_valid) === false && is_valid !== 'All' && is_valid !== undefined) {
		filters = { is_valid: false, ...getPureParams(rest) };
	}
	return convertFiltersToV2(filters);
};

/**
 * V2 Trip List Search Action
 * Uses POST /api/v2/search with v2 request/response format
 */
export const getTripListActionV2 = (params) => async () => {
	const {
		convertPaginationToV2,
		convertFiltersToV2,
		convertSortToV2,
		transformTripListResponseV2ToV1,
	} = await import('../utils/tripV2Transformations');

	const {
		search_type = 'trip',
		limit = 10,
		offset = 0,
		sort_column,
		sort_direction,
	} = params || {};

	const pagination = convertPaginationToV2(offset, limit);
	const v2Filters = buildTripFilterByV2History(params, convertFiltersToV2);
	const sort_by = convertSortToV2(sort_column, sort_direction);

	const requestData = { search_type, filter_by: v2Filters, pagination };
	if (sort_by) {
		requestData.sort_by = sort_by;
	}

	const CREDENTIALS = { url: '/search', data: requestData, version: 'v2' };

	return API.common(CREDENTIALS).then((response) => transformTripListResponseV2ToV1(response));
};

/**
 * V2 Filter Options Action
 * Uses POST /api/v2/search with search_type: 'city'/'client'/'project'/'vendor'
 */
export const getTripFilterListV2 = (filter) => async () => {
	const { transformFilterOptionsResponseV2ToV1 } = await import('../utils/tripV2Transformations');

	const CREDENTIALS = {
		url: '/search',
		data: {
			search_type: filter, // 'city', 'client', 'project', or 'vendor'
			pagination: {
				page: 1,
				per_page: 50,
			},
		},
		version: 'v2',
	};

	return API.common(CREDENTIALS).then((response) =>
		// Transform V2 response to V1 format
		transformFilterOptionsResponseV2ToV1(response, filter)
	);
};

/**
 * V2 Cross-dependent (cascading) filter options for a list_search type.
 *
 * @param {object} filterBy
 * @param {object} [opts]
 * @param {string}   [opts.listSearchType]  'trip' (default) | 'adhoc_request' | ...
 * @param {string[]} [opts.requestedFilters]
 * @param {number}   [opts.limit]
 */
export const getTripCascadingFilters =
	(filterBy = {}, opts = {}) =>
	async () => {
		const data = {
			list_search_type: opts.listSearchType || 'trip',
			filter_by: {
				limit: opts.limit || 50,
				...(opts.requestedFilters ? { requested_filters: opts.requestedFilters } : {}),
				...filterBy,
			},
		};
		const response = await API.common({
			url: '/list_search',
			data,
			version: 'v2',
			showLoading: false,
		});
		return response?.data || {};
	};

/**
 * V2 Trip Location History Action
 * Uses POST /api/v2/search with search_type: 'trip_location_history'
 */
export const getTripLocationHistoryV2 =
	({ trip_id, from_time, to_time, limit = 100, offset = 0 }) =>
	async () => {
		const {
			convertPaginationToV2,
			convertMomentToV2Date,
			transformLocationHistoryResponseV2ToV1,
		} = await import('../utils/tripV2Transformations');

		const pagination = convertPaginationToV2(offset, limit);

		const filter_by = {
			trip_id,
		};

		// Add optional time range filters
		if (from_time) {
			filter_by.from_time =
				convertMomentToV2Date(from_time) || convertMomentToV2Date(moment(from_time));
		}
		if (to_time) {
			filter_by.to_time =
				convertMomentToV2Date(to_time) || convertMomentToV2Date(moment(to_time));
		}

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'trip_location_history',
				filter_by,
				pagination,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) =>
			// Transform V2 response to V1 format
			transformLocationHistoryResponseV2ToV1(response)
		);
	};

/**
 * V2 Trip Status History Action
 * Uses POST /api/v2/search with search_type: 'trip_status_history'
 */
export const getTripStatusHistoryV2 =
	({ trip_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'trip_status_history',
				filter_by: {
					trip_id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => response || {});
	};

/**
 * Export History trips to CSV. Accepts the SAME params object the History search uses
 * (e.g. { ...paginateConfig, trip_category:'history', business_unit_ids, from_date, to_date }),
 * NOT a hand-built filter_data subset → guaranteed row parity with the page.
 */
export const exportTripListV2 = (params) => async () => {
	const { convertFiltersToV2 } = await import('../utils/tripV2Transformations');
	const filter_by = buildTripFilterByV2History(params, convertFiltersToV2);

	const CREDENTIALS = {
		url: '/report',
		data: {
			report_type: 'trip_csv_export',
			format: 'csv',
			data: { filter_by },
		},
		version: 'v2',
		aclBlockApiCall: false,
	};

	return API.common(CREDENTIALS).then((res) => {
		const downloadUrl = res?.data?.download_url;
		if (downloadUrl) {
			window.open(downloadUrl, '_blank', 'noopener,noreferrer');
		}
		return {
			rows: res?.data || {},
		};
	});
};

/**
 * V2 Get Trip Margin Statement Action
 * Uses POST /api/v2/search with search_type: 'trip_margin_statement'
 */
export const getTripMarginStatementV2 =
	({ trip_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'trip_margin_statement',
				filter_by: {
					trip_id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) =>
			// V2 returns { status: "success", data: [...] }
			// Return in format compatible with component expectations
			({
				data: response?.data || [],
				rows: response?.data || [],
			})
		);
	};

/**
 * V2 Search Proof of Delivery Action
 * Uses POST /api/v2/search with search_type: 'proof_of_delivery'
 * According to API contract, limit and offset are in filter_by
 * @param {Number} trip_id - Filter by specific trip
 * @param {String} status - Filter by POD status (pending/uploaded/verified/rejected)
 * @param {Number} consignment_note_id - Filter by consignment note
 * @param {String} query_str - Search in POD ID, trip number, project name
 * @param {Number} limit - Pagination limit (default: 10)
 * @param {Number} offset - Pagination offset (default: 0)
 * @returns {Promise} API response with POD data array
 */
export const searchProofOfDeliveryV2 =
	({ trip_id, status, consignment_note_id, query_str, limit = 10, offset = 0 }) =>
	async () => {
		const filter_by = {};

		if (trip_id) {
			filter_by.trip_id = trip_id;
		}
		if (status) {
			filter_by.status = status;
		}
		if (consignment_note_id) {
			filter_by.consignment_note_id = consignment_note_id;
		}
		if (query_str) {
			filter_by.query_str = query_str;
		}

		// According to API contract, limit and offset are in filter_by
		filter_by.limit = Number(limit) || 10;
		filter_by.offset = Number(offset) || 0;

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'proof_of_delivery',
				filter_by,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) =>
			// V2 returns { status: "success", data: [{ id, ... }], total_count: 1 }
			// Return in format compatible with component expectations
			({
				rows: response?.data || [],
				data: response?.data || [],
				count: response?.total_count || 0,
			})
		);
	};

/**
 * V2 Get POD URLs Action
 * Uses POST /api/v2/search with search_type: 'proof_of_delivery_url'
 */
export const getPodUrlsV2 =
	({ id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'proof_of_delivery_url',
				filter_by: {
					id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then(
			(response) =>
				// V2 returns { status: "success", data: { proof_of_delivery_url, ... } }
				// Return data directly for component compatibility
				response?.data || {}
		);
	};

/**
 * Helper: Transform V2 goods item to V1 format
 * @param {Object} item - V2 goods item with name, quantity, weight, dimensions, model
 * @returns {Object} V1 goods item with goods_description, goods_quantity, etc.
 */
const transformV2GoodsItemToV1 = (item) => {
	const goodsItem = {
		goods_description: item.name || '',
		goods_quantity: item.quantity || '',
		goods_weight: item.weight || '',
		goods_uom: item.model || '',
		goods_length: '',
		goods_width: '',
		goods_height: '',
	};

	// Parse dimensions string (e.g., "10x20x30") back to separate fields
	if (item.dimensions && typeof item.dimensions === 'string') {
		const dims = item.dimensions.split('x');
		if (dims.length >= 1) goodsItem.goods_length = dims[0].trim();
		if (dims.length >= 2) goodsItem.goods_width = dims[1].trim();
		if (dims.length >= 3) goodsItem.goods_height = dims[2].trim();
	}

	return goodsItem;
};

/**
 * Helper: Normalize V1 goods item to ensure all fields are present
 * @param {Object} item - Goods item (may have V1 or V2 field names)
 * @returns {Object} Normalized V1 goods item
 */
const normalizeV1GoodsItem = (item) => ({
	goods_description: item.goods_description || item.name || '',
	goods_quantity: item.goods_quantity || item.quantity || '',
	goods_weight: item.goods_weight || item.weight || '',
	goods_uom: item.goods_uom || item.model || '',
	goods_length: item.goods_length || '',
	goods_width: item.goods_width || '',
	goods_height: item.goods_height || '',
});

/**
 * Helper: Transform goods_details from V2 to V1 format
 * @param {Object|Array} goodsDetails - V2 JSONB format or V1 array format
 * @returns {Array} V1 array format
 */
const transformGoodsDetailsToV1 = (goodsDetails) => {
	if (!goodsDetails) {
		return [];
	}

	// Already in V1 array format
	if (Array.isArray(goodsDetails)) {
		return goodsDetails.map(normalizeV1GoodsItem);
	}

	// V2 JSONB format: { items: [...] }
	if (goodsDetails.items && Array.isArray(goodsDetails.items)) {
		return goodsDetails.items.map(transformV2GoodsItemToV1);
	}

	// Invalid format, default to empty array
	return [];
};

/**
 * Helper: Transform a single consignment note from V2 to V1 format
 * @param {Object} note - V2 consignment note
 * @returns {Object} V1 consignment note
 */
const transformConsignmentNoteToV1 = (note) => ({
	...note,
	goods_details: transformGoodsDetailsToV1(note.goods_details),
});

/**
 * V2 Get Consignment Note/LR Action
 * Uses POST /api/v2/search with search_type: 'consignment_note' to get LR/consignment note data
 * @param {String|Number} trip_id - Trip ID to filter consignment notes
 * @param {Number} limit - Pagination limit (default: 10)
 * @param {Number} offset - Pagination offset (default: 0)
 * @returns {Promise} API response with consignment note data
 */
export const getConsignmentNoteV2 =
	({ trip_id, limit = 10, offset = 0 }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'consignment_note',
				offset: Number(offset) || 0,
				limit: Number(limit) || 10,
				fetch_filters: true,
				filter_list: {},
				filter_by: {
					trip_id: String(trip_id),
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const consignmentNotes = response?.data || [];
			const transformedNotes = consignmentNotes.map(transformConsignmentNoteToV1);

			return {
				rows: transformedNotes,
				data: transformedNotes,
				count: response?.total_count || transformedNotes.length || 0,
			};
		});
	};

/**
 * Helper: Transform a single goods detail item to V2 format
 * @param {Object} item - Form item with goods fields
 * @returns {Object} Transformed item for V2 API
 */
const transformGoodsItemToV2 = (item) => {
	const transformedItem = {
		name: item.goods_description || '',
		quantity: item.goods_quantity ? Number(item.goods_quantity) : undefined,
		weight: item.goods_weight ? Number(item.goods_weight) : undefined,
	};

	// Combine dimensions if available
	const dimensions = [];
	if (item.goods_length) dimensions.push(`${item.goods_length}`);
	if (item.goods_width) dimensions.push(`${item.goods_width}`);
	if (item.goods_height) dimensions.push(`${item.goods_height}`);
	if (dimensions.length > 0) {
		transformedItem.dimensions = dimensions.join('x');
	}

	// Add UOM as model if available
	if (item.goods_uom) {
		transformedItem.model = item.goods_uom;
	}

	// Remove undefined values
	Object.keys(transformedItem).forEach((key) => {
		if (transformedItem[key] === undefined) {
			delete transformedItem[key];
		}
	});

	return transformedItem;
};

/**
 * Helper: Transform goods_details from form array format to V2 API JSONB format
 * @param {Array} goodsDetailsArray - Form array: [{ goods_description, goods_quantity, goods_uom, goods_length, goods_width, goods_height, goods_weight }]
 * @returns {Object|null} V2 API format: { items: [{ name, quantity, weight, dimensions }], packaging?, special_instructions? }
 */
const transformGoodsDetailsToV2 = (goodsDetailsArray) => {
	if (!Array.isArray(goodsDetailsArray) || goodsDetailsArray.length === 0) {
		return null;
	}

	const items = goodsDetailsArray
		.filter((item) => item && item.goods_description) // Filter out empty items
		.map(transformGoodsItemToV2);

	if (items.length === 0) {
		return null;
	}

	return {
		items,
		// Optional fields can be added here if available in form
		// packaging: ...,
		// special_instructions: ...,
	};
};

/**
 * Helper: Build V2 consignment note data object
 * @param {Object} consignmentData - Raw consignment data from form
 * @param {Object} transformedGoodsDetails - Transformed goods_details
 * @param {Boolean} isEditMode - Whether this is an edit operation
 * @returns {Object} V2 API data object
 */
const buildV2ConsignmentData = (consignmentData, transformedGoodsDetails, isEditMode) => {
	const v2Data = {
		...(isEditMode && consignmentData.id ? { id: consignmentData.id } : {}),
		trip_id: consignmentData.trip_id,
		project_id: consignmentData.project_id,
		vendor_id: consignmentData.vendor_id,
		sender_name: consignmentData.sender_name,
		sender_address: consignmentData.sender_address,
		sender_pincode: String(consignmentData.sender_pincode),
		source_location: consignmentData.source_location,
		receiver_name: consignmentData.receiver_name,
		receiver_address: consignmentData.receiver_address,
		receiver_pincode: String(consignmentData.receiver_pincode),
		destination_location: consignmentData.destination_location,
		vehicle_number: consignmentData.vehicle_number,
		driver_name: consignmentData.driver_name,
		goods_description: consignmentData.goods_description,
		declared_value: Number(consignmentData.declared_value),
		gross_weight: Number(consignmentData.gross_weight),
	};

	// Add optional fields conditionally
	if (
		consignmentData.client_reference_numbers &&
		Array.isArray(consignmentData.client_reference_numbers) &&
		consignmentData.client_reference_numbers.length > 0
	) {
		v2Data.client_reference_numbers = consignmentData.client_reference_numbers;
	}

	if (consignmentData.ewb_number) {
		v2Data.ewb_number = consignmentData.ewb_number;
	}

	if (consignmentData.unit_count) {
		v2Data.unit_count = Number(consignmentData.unit_count);
	}

	if (transformedGoodsDetails) {
		v2Data.goods_details = transformedGoodsDetails;
	}

	return v2Data;
};

/**
 * Create Consignment Note V2
 * Endpoint: POST /api/v2/create
 * @param {Object} consignmentData - Consignment note data from form
 * @param {Function} setErrors - Error handler function
 * @param {Boolean} isEditMode - Whether this is an edit operation
 * @returns {Promise} API response
 *
 * Request structure:
 * {
 *   create_type: "consignment_note",
 *   data: {
 *     trip_id: 12345,
 *     project_id: 10,
 *     vendor_id: 5,
 *     sender_name: "ABC Warehouse",
 *     sender_address: "123 Industrial Area, Sector 5, Mumbai",
 *     sender_pincode: "400001",
 *     source_location: "Mumbai Central",
 *     receiver_name: "XYZ Store",
 *     receiver_address: "456 Market Street, Camp Area, Pune",
 *     receiver_pincode: "411001",
 *     destination_location: "Pune City",
 *     client_reference_numbers: ["REF12345"],
 *     ewb_number: "123456789012",
 *     vehicle_number: "MH12AB1234",
 *     driver_name: "Ramesh Kumar",
 *     goods_description: "Electronics",
 *     declared_value: 50000.00,
 *     gross_weight: 1500.50,
 *     unit_count: 25,
 *     goods_details: { items: [...] }
 *   }
 * }
 */
export const createConsignmentNoteV2 =
	({ consignmentData, setErrors, isEditMode = false }) =>
	async () => {
		// Transform goods_details from form array to V2 JSONB format
		const transformedGoodsDetails = transformGoodsDetailsToV2(consignmentData.goods_details);

		// Build V2 consignment note data
		const v2Data = buildV2ConsignmentData(consignmentData, transformedGoodsDetails, isEditMode);

		// Build request data
		const requestData = {
			create_type: 'consignment_note',
			data: v2Data,
		};

		// For edit mode, use update endpoint
		if (isEditMode && consignmentData.id) {
			requestData.update_type = 'consignment_note';
		}

		const CREDENTIALS = {
			url: isEditMode ? '/update' : '/create',
			data: requestData,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success') {
				const messageText = isEditMode
					? response.message || 'Consignment note updated successfully'
					: response.message || 'Consignment note created successfully';
				message.success(messageText);
			}
			return response;
		});
	};

/**
 * Helper function to show trip generation result messages
 * @param {number} trips_created - Number of trips created
 * @param {number} trips_skipped - Number of trips skipped
 * @param {Array} errors - Array of error messages
 * @param {string} date - Date for which trips were generated
 */
const showTripGenerationMessages = (trips_created, trips_skipped, errors, date) => {
	if (trips_created > 0) {
		const skippedText = trips_skipped > 0 ? `, ${trips_skipped} skipped` : '';
		message.success(
			`Successfully generated ${trips_created} trip${
				trips_created > 1 ? 's' : ''
			}${skippedText}`
		);
	} else if (trips_skipped > 0) {
		message.warning(
			`No trips created. ${trips_skipped} trip${trips_skipped > 1 ? 's' : ''} skipped`
		);
	} else {
		const dateText = date ? ` for ${date}` : '';
		message.warning(
			`No trips were generated${dateText}. This may be because trips already exist for this date or the route is inactive.`
		);
	}

	if (errors && errors.length > 0) {
		console.warn('Trip generation errors:', errors);
		message.warning(`Some errors occurred: ${errors.join(', ')}`);
	}
};

/**
 * Generate Trips from Route Week Off V2
 * Endpoint: POST /api/v2/create
 * @param {Object} params - Generation parameters
 * @param {number} params.route_id - Route ID to generate trips for
 * @param {Function} params.setErrors - Error handler function (optional)
 * @returns {Promise} API response with trips_created, trips_skipped, and errors
 */
export const generateTripsFromRouteWeekOffV2 =
	({ route_id, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/create',
			data: {
				create_type: 'trip_from_route_week_off',
				data: {
					route_id,
				},
			},
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status !== 'success') {
				return response;
			}

			const {
				trips_created = 0,
				trips_skipped = 0,
				errors = [],
				date = null,
			} = response?.data || {};

			showTripGenerationMessages(trips_created, trips_skipped, errors, date);

			return response;
		});
	};

/**
 * V2 Get All Tracking Info Action
 * Uses POST /api/v2/search with search_type: 'all_tracking_info'
 */
export const getAllTrackingInfoV2 =
	({ trip_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'all_tracking_info',
				filter_by: {
					trip_id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => response || {});
	};

/**
 * Replace the vehicle on a trip (same or different vendor).
 * Endpoint: POST /api/v2/update with update_type: 'trip_replace_vehicle'
 *
 * @param {Object} params
 * @param {number} params.trip_id - required
 * @param {boolean} params.is_different_vendor - "Select different vendor" checkbox
 * @param {number} params.new_vehicle_id - required, "Replacement Vehicle" dropdown
 * @param {number} [params.new_driver_id] - optional same-vendor / sent for diff-vendor
 * @param {number} params.old_vehicle_odo_at_swap - required, old vehicle odometer @ swap
 * @param {number} params.new_vehicle_start_odo - required, new vehicle start odometer
 * @param {string} params.reason - required free-text reason
 * @param {number} [params.new_vendor_id] - required iff different vendor
 * @param {number} [params.new_vehicle_type_id] - required iff different vendor
 * @param {string} [params.trip_cost_mode] - "same" | "updated" (diff-vendor only)
 * @param {number} [params.new_rate] - FLAT billing, iff trip_cost_mode="updated"
 * @param {number} [params.new_per_km] - VARIABLE billing, iff trip_cost_mode="updated"
 * @param {Function} [params.setErrors] - Error handler (optional)
 * @returns {Promise} API response (data may include deduction_amount/deduction_status)
 */
export const replaceTripVehicleV2 =
	({
		trip_id,
		is_different_vendor,
		new_vehicle_id,
		new_driver_id,
		old_vehicle_odo_at_swap,
		new_vehicle_start_odo,
		reason,
		new_vendor_id,
		new_vehicle_type_id,
		trip_cost_mode,
		new_rate,
		new_per_km,
		order_rate_override,
		new_fixed_rate_per_day,
		setErrors,
	}) =>
	async () => {
		const isPresent = (v) => v !== undefined && v !== null;
		const isUpdated = is_different_vendor && trip_cost_mode === 'updated';

		const data = {
			trip_id,
			is_different_vendor: Boolean(is_different_vendor),
			new_vehicle_id,
			old_vehicle_odo_at_swap,
			new_vehicle_start_odo,
			reason,
			// Driver is optional for same-vendor; send whenever one is selected.
			...(isPresent(new_driver_id) ? { new_driver_id } : {}),
			// Vendor/type/cost block is only relevant for a different-vendor swap.
			...(is_different_vendor ? { new_vendor_id, new_vehicle_type_id, trip_cost_mode } : {}),
			// FLAT sends new_rate, PER KM sends new_per_km, PER ORDER sends
			// order_rate_override[] ({ rate_id, new_rate }) — updated-cost swaps only.
			...(isUpdated && isPresent(new_rate) ? { new_rate } : {}),
			...(isUpdated && isPresent(new_per_km) ? { new_per_km } : {}),
			...(isUpdated && Array.isArray(order_rate_override) && order_rate_override.length
				? { order_rate_override }
				: {}),
			// Fixed Per Day + Per Order carries an operator-entered daily rate on an
			// updated-cost, different-vendor swap.
			...(isUpdated && isPresent(new_fixed_rate_per_day) ? { new_fixed_rate_per_day } : {}),
		};

		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'trip_replace_vehicle',
				data,
			},
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success') {
				message.success(response.message || 'Vehicle replaced successfully');
			}
			return response;
		});
	};

/**
 * Replace the driver on a trip.
 * Endpoint: POST /api/v2/update with update_type: 'trip_replace_driver'
 * @param {Object} params
 * @param {number} params.trip_id - Trip whose driver is being replaced
 * @param {number} params.new_driver_id - Driver selected in "Replace Driver with"
 * @param {string} params.reason - Required free-text reason
 * @param {Function} [params.setErrors] - Error handler (optional)
 * @returns {Promise} API response
 */
export const replaceTripDriverV2 =
	({ trip_id, new_driver_id, reason, setErrors }) =>
	async () => {
		const CREDENTIALS = {
			url: '/update',
			data: {
				update_type: 'trip_replace_driver',
				data: {
					trip_id,
					new_driver_id,
					reason,
				},
			},
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success') {
				message.success(response.message || 'Driver replaced successfully');
			}
			return response;
		});
	};

/**
 * Fetch vehicle & driver replacement history for a trip.
 * Endpoint: POST /api/v2/search with search_type: 'trip_replacement_history'
 * @param {Object} params
 * @param {number} params.trip_id - Trip to fetch replacement history for
 * @returns {Promise<{vehicle: Array, driver: Array, meta: Object}>}
 */
export const getTripReplacementHistoryV2 =
	({ trip_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'trip_replacement_history',
				filter_by: { trip_id },
			},
			version: 'v2',
			showLoading: false,
		};

		return API.common(CREDENTIALS).then((response) => ({
			vehicle: response?.data?.vehicle_replacements || [],
			driver: response?.data?.driver_replacements || [],
			meta: response?.meta || {},
		}));
	};

/**
 * V2 Trip Map Path Action
 * Uses POST /api/v2/search with search_type: 'trip_map_path'
 *
 * Returns the actual travelled GPS trail for a trip plus the vehicle's current position.
 * Note the payload uses lat/lng/time, NOT the latitude/longitude/timestamp naming used by
 * `all_tracking_info` — points are normalized here so callers only ever see one shape.
 *
 * The response is not paginated: `path` is the complete trail for the trip. Trips with no
 * GPS data yet return an empty path and a null current_location, which is a valid state.
 *
 * @param {Object} params
 * @param {number|string} params.trip_id - required
 * @returns {Promise<{path: Array<{lat: number, lng: number, time: string|null}>,
 *   currentLocation: {lat: number, lng: number, time: string|null}|null, count: number}>}
 */
export const getTripMapPathV2 =
	({ trip_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'trip_map_path',
				filter_by: { trip_id },
			},
			version: 'v2',
			// This action polls; the global loader must not flash on every tick.
			showLoading: false,
		};

		return API.common(CREDENTIALS).then((response) => {
			const data = response?.data || {};
			const path = (Array.isArray(data.path) ? data.path : [])
				.map(normalizeMapPathPoint)
				.filter(Boolean);

			// Defensive: the contract says ascending by time, sorting keeps a bad feed harmless.
			path.sort((a, b) => String(a.time ?? '').localeCompare(String(b.time ?? '')));

			return {
				path,
				currentLocation: normalizeMapPathPoint(data.current_location),
				count: response?.meta?.count ?? path.length,
			};
		});
	};

/**
 * V2 Trip Distance Reconciliation.
 * POST /api/v2/search with search_type: 'trip_distance_reconciliation'.
 *
 * Live on the backend's `feat/trip-distance-optim` branch (routed through app.py ->
 * service_controllers_v2.py -> tracking_controller.py -> distance_reconciliation_service.py),
 * and wired up here via USE_RECONCILIATION_API in
 * pages/trip/components/distance/constants.js, which is already set to true. Until that
 * backend branch is merged to an environment this is pointed at, calls will fail there;
 * once it lands, the Distance tab's view model is built from this response instead of
 * falling back to `all_tracking_info` alone.
 *
 * Returns the raw `data` object: { window, distances, gps, source_tag, stoppages, is_final }.
 * Shaping lives in buildDistanceViewModel so one function owns the null-vs-zero rules for
 * both this payload and the all_tracking_info fallback.
 *
 * Contract: docs/trip-distance-tab/TRIP_DISTANCE_TAB.md §5.1
 *
 * @param {Object} params
 * @param {number|string} params.trip_id - required
 * @returns {Promise<Object|null>}
 */
export const getTripDistanceReconciliationV2 =
	({ trip_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'trip_distance_reconciliation',
				filter_by: { trip_id },
			},
			version: 'v2',
			// This action polls while a trip is open; the global loader must not flash.
			showLoading: false,
		};

		return API.common(CREDENTIALS).then((response) => response?.data || null);
	};

export default tripSliceV2.reducer;
